import { z } from 'zod';
import { healthAppStatusSchema, healthComponentSchema, healthDeployOutcomeSchema, healthDeploymentPhaseSchema, healthEnvironmentSchema, healthFailureSchema, healthNotConnectedReasonSchema, healthSourceSchema, } from './health.js';
/**
 * 0.26.0: Trace Admin's Apps pages (GET /v1/admin/apps, GET /v1/admin/apps/{appId}) and their controls (POST
 * /v1/admin/apps/{appId}/operations). The DigitalOcean App Platform apps of this platform-api's own environment (its
 * DigitalOcean project only), read with Health's read-only token, and two controls sent with a separate control
 * token (custom scope app:update, which DigitalOcean requires for both; platform-api calls nothing else with it). A convenience, not a DigitalOcean console: logs, the app spec, scaling,
 * metrics history and stopping an app stay in DigitalOcean, linked from each page.
 *
 * No crossover between environments: each Trace Admin acts only on the apps of its own environment's DigitalOcean
 * project (platform-api checks every app against it), with its own environment's token.
 *
 * What each control does on App Platform (DigitalOcean's API reference, 2026-10-28):
 * - restart: POST /v2/apps/{id}/restart. A rolling restart of the services and workers (or the ones named) on the
 *   version they run; jobs do not run.
 * - redeploy: POST /v2/apps/{id}/deployments. Deploys the current spec again: the same images, the pre-deploy jobs
 *   (migrations) run again. It picks up no new code: Trace's images are built by GitHub Actions. forceBuild rebuilds
 *   components built from Git (none of Trace's apps today).
 * Stop is not a control (decision of 2026-10-28): App Platform can only stop an app by rewriting its spec
 * (maintenance.archive), which needs a token that can rewrite any app's spec and races the deploy workflow. The app
 * page links to the app's settings in DigitalOcean, where its own Archive lives.
 */
/** What staff see an app doing, from its spec and deployments. */
export const PLATFORM_APP_STATES = [
    'running',
    'deploying',
    'archived',
    'maintenance',
    'not_deployed',
    'unknown',
];
export const platformAppStateSchema = z.enum(PLATFORM_APP_STATES);
/** Why the controls are off in this environment: no control token, or Health is not connected (no app to act on). */
export const PLATFORM_APP_CONTROL_UNAVAILABLE_REASONS = ['no_control_token', 'not_connected'];
export const platformAppControlsSchema = z.object({
    available: z.boolean(),
    reason: z.enum(PLATFORM_APP_CONTROL_UNAVAILABLE_REASONS).nullable(),
});
/** A deployment of an app, with its progress while it runs. */
export const platformAppDeploymentSchema = z.object({
    id: z.string(),
    phase: healthDeploymentPhaseSchema,
    outcome: healthDeployOutcomeSchema,
    /** DigitalOcean's own words for why it deployed (for example "manual", "app spec updated"). */
    cause: z.string().nullable(),
    createdAt: z.iso.datetime(),
    /** When DigitalOcean last changed it; null when it did not say. */
    updatedAt: z.iso.datetime().nullable(),
    /** The commit deployed: the image tag's commit, or the source commit for apps built from Git. */
    commit: z.string().nullable(),
    /** Its steps (build, deploy, jobs): done of total, and failed. Null when DigitalOcean reported none. */
    progress: z
        .object({ done: z.number().int(), total: z.number().int(), failed: z.number().int() })
        .nullable(),
    /** The deployment in DigitalOcean's control panel (its logs and steps). */
    consoleUrl: z.string(),
});
/** A row of the Apps list. */
export const platformAppSummarySchema = z.object({
    id: z.string(),
    /** DigitalOcean's name of the app (trace-platform-dev). */
    name: z.string(),
    /** The name people know it by (Health's names). */
    displayName: z.string(),
    host: z.string().nullable(),
    /** Health's judgement of it (the same rules as the Health page). */
    status: healthAppStatusSchema,
    reasons: z.array(z.string()),
    state: platformAppStateSchema,
    /** The services and workers as App Platform's health read reports them now. */
    components: z.array(healthComponentSchema),
    /** The deployment serving now. */
    deployment: platformAppDeploymentSchema.nullable(),
    /** A deployment under way right now. */
    inProgress: platformAppDeploymentSchema.nullable(),
    consoleUrl: z.string(),
});
/** GET /v1/admin/apps: this environment's apps (from Health's cached reads: no DigitalOcean request of its own). */
export const adminPlatformAppsSchema = z.object({
    environment: healthEnvironmentSchema,
    source: healthSourceSchema,
    connected: z.boolean(),
    notConnectedReason: healthNotConnectedReasonSchema.nullable(),
    /** When platform-api read DigitalOcean. */
    checkedAt: z.iso.datetime(),
    controls: platformAppControlsSchema,
    failures: z.array(healthFailureSchema),
    apps: z.array(platformAppSummarySchema),
});
export const PLATFORM_APP_COMPONENT_KINDS = ['service', 'worker', 'job', 'static_site', 'function'];
export const platformAppComponentKindSchema = z.enum(PLATFORM_APP_COMPONENT_KINDS);
/** One component of an app as its spec defines it, with its health now (services and workers). */
export const platformAppComponentSchema = z.object({
    name: z.string(),
    kind: platformAppComponentKindSchema,
    /** The instance size slug (apps-s-1vcpu-0.5gb); null for static sites and functions. */
    instanceSize: z.string().nullable(),
    /** The fixed instance count; null when autoscaled or not applicable. */
    instanceCount: z.number().int().nullable(),
    autoscaling: z.object({ min: z.number().int(), max: z.number().int() }).nullable(),
    /** Where it comes from: a container image, or source built by App Platform from Git. */
    source: z.enum(['image', 'git']).nullable(),
    /** A job's kind as DigitalOcean names it (PRE_DEPLOY, POST_DEPLOY, FAILED_DEPLOY, SCHEDULED). */
    jobKind: z.string().nullable(),
    /** Its state and instances now (services and workers); null for the others or when the read failed. */
    health: healthComponentSchema.nullable(),
    /** Whether restart can name it: services and workers only. */
    restartable: z.boolean(),
    /** Its runtime logs in DigitalOcean's control panel. */
    logsUrl: z.string(),
});
/** One app in full: the Apps detail page. */
export const platformAppDetailSchema = platformAppSummarySchema.omit({ components: true }).extend({
    /** The region slug (tor, nyc). */
    region: z.string().nullable(),
    liveUrl: z.string().nullable(),
    createdAt: z.iso.datetime().nullable(),
    maintenance: z.object({ enabled: z.boolean(), archived: z.boolean() }),
    components: z.array(platformAppComponentSchema),
    /** The latest deployments, newest first (at most ten). */
    recentDeploys: z.array(platformAppDeploymentSchema),
    /** Where the rest lives in DigitalOcean's control panel. */
    links: z.object({ console: z.string(), logs: z.string(), settings: z.string(), activity: z.string() }),
});
/** GET /v1/admin/apps/{appId}. */
export const adminPlatformAppSchema = z.object({
    environment: healthEnvironmentSchema,
    source: healthSourceSchema,
    checkedAt: z.iso.datetime(),
    controls: platformAppControlsSchema,
    failures: z.array(healthFailureSchema),
    app: platformAppDetailSchema,
});
/** DigitalOcean's ids are UUIDs; anything else is refused before a request is made. */
export const platformAppIdSchema = z.uuid();
const componentNameSchema = z.string().regex(/^[a-z0-9][a-z0-9-]{0,62}$/, 'a component name');
export const PLATFORM_APP_OPERATIONS = ['restart', 'redeploy'];
export const platformAppOperationSchema = z.enum(PLATFORM_APP_OPERATIONS);
/** POST /v1/admin/apps/{appId}/operations: restart or redeploy. */
export const platformAppOperationRequestSchema = z.discriminatedUnion('action', [
    z
        .object({
        action: z.literal('restart'),
        /** The services or workers to restart; all of them when left out. */
        components: z.array(componentNameSchema).min(1).max(20).optional(),
    })
        .strict(),
    z.object({ action: z.literal('redeploy'), forceBuild: z.boolean().default(false) }).strict(),
]);
/** What an operation started: the deployment DigitalOcean created, to follow until it ends (null: none named). */
export const platformAppOperationResultSchema = z.object({
    action: platformAppOperationSchema,
    appId: z.string(),
    deployment: platformAppDeploymentSchema.nullable(),
});
/** GET /v1/admin/apps/{appId}/deployments/{deploymentId}: one deployment, polled while it runs. */
export const adminPlatformAppDeploymentSchema = z.object({
    appId: z.string(),
    checkedAt: z.iso.datetime(),
    deployment: platformAppDeploymentSchema,
});
/** True while a deployment is still under way (the page follows it until then). */
export function deploymentRunning(deployment) {
    return deployment.outcome === 'in_progress';
}
//# sourceMappingURL=platform-apps.js.map